import React, { useContext, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../Auth/AuthContext/AuthContext';
import { useIsContributor } from '../../hooks/useAccessControl';
import { GlobalSearchIcon } from '../GlobalSearch';
import { navigationConfig } from './navigationConfig';
import NavItem from './NavItem';
import ProfileAvatar from './ProfileAvatar';
import MobileMenu from './MobileMenu';
import sidebarCoral from '../../assets/images/sidebar/sidebar-coral.png';

const Sidebar: React.FC<{ className?: string }> = ({ className }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, userProfile } = useContext(AuthContext);
    const isContributor = useIsContributor();


    async function handleSignOut() {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Error signing out: ', error);
        }
    }

    // Filter nav items based on contributor status - memoized to prevent recalculation
    const filteredPrimaryItems = useMemo(() =>
        navigationConfig.primary.filter(item =>
            !item.requiresContributor || isContributor
        ), [isContributor]
    );

    const filteredSecondaryItems = useMemo(() =>
        navigationConfig.secondary.filter(item =>
            !item.requiresContributor || isContributor
        ), [isContributor]
    );

    // Mobile visible items (impact, listening room, notifications, search) - memoized
    const mobileVisibleItems = useMemo(() =>
        filteredPrimaryItems.filter(item =>
            item.id === 'impact' || item.id === 'listening' || item.id === 'notifications'
        ), [filteredPrimaryItems]
    );
    
    return (
        <div className={`bg-[#2D3846] flex flex-row sm:flex-col items-center sm:py-10 sm:px-4 sm:w-20 ${className}`}>
            {/* MOBILE LAYOUT (horizontal) */}
            <div className="flex flex-row items-center justify-between w-full px-4 py-2 sm:hidden">
                {/* Left side - Logo and primary actions */}
                <div className="flex items-center space-x-3">
                    <Link to="/dashboard">
                        <img src={sidebarCoral} alt="wearecoral logo" className="w-10 h-10" />
                    </Link>

                    {/* Impact - Primary action on mobile */}
                    {mobileVisibleItems.find(item => item.id === 'impact') && (
                        <NavItem
                            item={mobileVisibleItems.find(item => item.id === 'impact')!}
                            isActive={location.pathname === '/impact'}
                        />
                    )}

                    {/* Listening Room - Primary action on mobile */}
                    {userProfile && isContributor && mobileVisibleItems.find(item => item.id === 'listening') && (
                        <NavItem
                            item={mobileVisibleItems.find(item => item.id === 'listening')!}
                            isActive={location.pathname.startsWith('/listening-room')}
                        />
                    )}
                </div>

                {/* Right side - Secondary actions and menu */}
                <div className="flex items-center space-x-2">
                    {/* Notifications */}
                    {userProfile && isContributor && mobileVisibleItems.find(item => item.id === 'notifications') && (
                        <NavItem
                            item={mobileVisibleItems.find(item => item.id === 'notifications')!}
                            isActive={location.pathname === '/notifications'}
                        />
                    )}

                    {/* Search */}
                    {userProfile && isContributor && (
                        <GlobalSearchIcon className="text-[#73869E] rounded-lg border border-transparent hover:bg-[#3D4758] hover:border-coral-pink/20 transition-[background-color,border-color] duration-150" />
                    )}

                    {/* Hamburger Menu */}
                    {userProfile && isContributor && (
                        <MobileMenu userProfile={userProfile} onSignOut={handleSignOut} />
                    )}
                </div>
            </div>

            {/* DESKTOP LAYOUT (vertical) - Hidden on mobile */}
            <div className="hidden sm:flex sm:flex-col sm:items-center sm:h-full sm:justify-between sm:w-full">
                {/* Top section */}
                <div className="flex flex-col items-center">
                    {/* Logo */}
                    <Link to="/dashboard" className="mb-10">
                        <img src={sidebarCoral} alt="wearecoral logo" className="w-10 h-10" />
                    </Link>

                    {/* Profile Avatar */}
                    {userProfile && isContributor && (
                        <div className="mb-6">
                            <ProfileAvatar userProfile={userProfile} />
                        </div>
                    )}

                    {/* Primary Navigation */}
                    <div className="flex flex-col space-y-3">
                        {filteredPrimaryItems.map((item) => (
                            <NavItem
                                key={item.id}
                                item={item}
                                isActive={item.id === 'listening' ? location.pathname.startsWith(item.path) : location.pathname === item.path}
                            />
                        ))}

                        {/* Search */}
                        {userProfile && isContributor && (
                            <GlobalSearchIcon className="text-[#73869E] rounded-lg border border-transparent hover:bg-[#3D4758] hover:border-coral-pink/20 transition-[background-color,border-color] duration-150" />
                        )}
                    </div>
                </div>

                {/* Bottom section */}
                <div className="flex flex-col items-center space-y-4">
                    {/* Secondary Navigation */}
                    {filteredSecondaryItems.map((item) => (
                        <NavItem
                            key={item.id}
                            item={item}
                            isActive={location.pathname === item.path}
                        />
                    ))}

                    {/* Logout Button */}
                    <button
                        onClick={handleSignOut}
                        className="text-xs text-custom-gray hover:text-white transition-colors duration-200"
                    >
                        Logout
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(Sidebar);